/**
 * configRejection — turning a POST /config 400 into something a member can act
 * on, at the control they were using.
 *
 * ARC-54 decided the placement question: the engine's rejection renders INLINE,
 * on the card that caused it, IN ADDITION to the tab-level error banner. The
 * banner stays — it is the backstop that guarantees no failed save can ever look
 * like a save that worked. What the banner cannot do is tell a member who is
 * looking at the risk sliders that the thing they just pressed was refused; on a
 * long tab that message is off screen.
 *
 * The engine REJECTS unknown keys with a 400 that names them
 * (ENGINE_CONTRACTS_FOR_FRONTEND_2026-08-05, as amended), which makes an app/
 * engine version mismatch diagnosable from the screen instead of the console.
 *
 * Nothing here talks to the network — pure parsing and copy, so it is fully
 * unit-testable and carries no transport concerns.
 */

/**
 * Field names the key list may arrive under. The exact body shape isn't pinned
 * down in any contract doc reachable from this repo, so several plausible names
 * are accepted rather than one being guessed at. Narrow this to the real field
 * once the shape is confirmed.
 */
const KEY_FIELDS = ["keys", "unknownKeys", "rejectedKeys", "invalidKeys", "fields"];

/** Find the first `{...}` in a string and JSON.parse it. A body truncated by the
 *  transport's 200-char slice simply fails to parse and yields null. */
const extractJson = (message) => {
  if (typeof message !== "string") return null;
  const start = message.indexOf("{");
  if (start === -1) return null;
  try {
    const parsed = JSON.parse(message.slice(start));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const firstArrayField = (body, fields) => {
  for (const f of fields) {
    const v = body[f];
    if (Array.isArray(v) && v.length) return v.map(String).filter(Boolean);
  }
  return null;
};

/**
 * Pull the rejected key names out of a failed POST /config.
 *
 * `signalsApi`'s fetch wrapper is HUMAN-ONLY and today folds the response body
 * into `err.message` as a truncated string, so we read, in order:
 *   1. `err.body` — structured, present once patches/signalsApi-error-body.patch
 *      is applied by a human. Preferred, and never truncated.
 *   2. the first JSON object embedded in `err.message`.
 *
 * Returns null for anything that isn't a 400: an auth failure, a rate limit or
 * an unreachable engine are not contract rejections and must not be dressed up
 * as one. Those keep the tab-level banner and nothing more.
 *
 * @param {Error & {code?:string, status?:number, body?:object}} err
 * @returns {{keys:string[], detail:string|null, raw:string}|null}
 */
export function parseConfigRejection(err) {
  if (!err) return null;
  if (err.code !== "HTTP_400" && err.status !== 400) return null;

  const body = err.body || extractJson(err.message);
  const keys = body ? firstArrayField(body, KEY_FIELDS) : null;

  return {
    keys: keys || [],
    /* Keep the engine's own words available: if the shape ever changes we still
       show the member something true rather than an empty message. */
    detail: (body && (body.error || body.message || body.detail)) || null,
    raw: typeof err?.message === "string" ? err.message : String(err),
  };
}

/**
 * Member-facing sentence for a rejected save. States what the engine refused and
 * that nothing changed — no claim about money, outcomes or advice.
 */
export function describeConfigRejection(rejection) {
  if (!rejection) return null;
  if (rejection.keys.length) {
    return `The engine rejected this save because it didn't recognise ${
      rejection.keys.length === 1 ? "one of the settings" : "some of the settings"
    } it was sent. Nothing was changed. This is a version mismatch between the app and the engine — please report it.`;
  }
  return (
    rejection.detail ||
    "The engine rejected this save as invalid. Nothing was changed. Please report it if it keeps happening."
  );
}

/**
 * The whole thing in one call, for the two save handlers on the tab: a 400 with
 * its rejected keys, or null for every other failure.
 *
 * @returns {{message:string, keys:string[]}|null}
 */
export function toInlineRejection(err) {
  const parsed = parseConfigRejection(err);
  if (!parsed) return null;
  return { message: describeConfigRejection(parsed), keys: parsed.keys };
}
